'use client';

import { useEffect } from 'react';

import { AlertTriangle, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { navigateWithLocale } from '@/lib/i18n/locale-aware-path';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Application error:', error);
  }, [error]);

  return (
    <div className='flex min-h-screen items-center justify-center'>
      <div className='flex flex-col items-center space-y-6 text-center'>
        <div className='flex flex-col items-center space-y-4'>
          <AlertTriangle className='h-12 w-12 text-destructive' />
          <h1 className='text-2xl font-bold'>Something went wrong!</h1>
          <p className='text-muted-foreground max-w-md'>
            We encountered an unexpected error. Please try again or contact support if the problem
            persists.
          </p>
        </div>

        <div className='flex flex-col space-y-2'>
          <Button onClick={reset} className='flex items-center space-x-2'>
            <RefreshCw className='h-4 w-4' />
            <span>Try again</span>
          </Button>

          <Button variant='outline' onClick={() => navigateWithLocale('/')}>
            Go to homepage
          </Button>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <details className='mt-4 max-w-md'>
            <summary className='cursor-pointer text-sm text-muted-foreground'>
              Error details (development only)
            </summary>
            <pre className='mt-2 whitespace-pre-wrap text-xs text-muted-foreground'>
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
